interface SajuInfo {
  year_gan: string; year_ji: string
  month_gan: string; month_ji: string
  day_gan: string; day_ji: string
  time_gan: string | null; time_ji: string | null
  element_wood: number; element_fire: number
  element_earth: number; element_metal: number; element_water: number
}

interface Props {
  saju: SajuInfo | null
  nickname: string
  mbti?: string | null
  bloodType?: string | null
}

const ELEMENT_LABEL: Record<string, { color: string; bg: string; emoji: string }> = {
  목: { color: '#4ade80', bg: 'rgba(22,101,52,0.35)', emoji: '🌿' },
  화: { color: '#f87171', bg: 'rgba(127,29,29,0.35)', emoji: '🔥' },
  토: { color: '#fbbf24', bg: 'rgba(120,53,15,0.35)', emoji: '🌍' },
  금: { color: '#cbd5e1', bg: 'rgba(71,85,105,0.35)', emoji: '⚡' },
  수: { color: '#60a5fa', bg: 'rgba(30,58,138,0.35)', emoji: '💧' },
}

const GAN_ELEMENT: Record<string, string> = {
  갑: '목', 을: '목', 병: '화', 정: '화', 무: '토',
  기: '토', 경: '금', 신: '금', 임: '수', 계: '수',
}

export default function SajuCharacterCard({ saju, nickname, mbti, bloodType }: Props) {
  if (!saju) {
    return (
      <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 flex items-center justify-center h-48">
        <p className="text-[var(--color-text-tertiary)] text-sm">사주 정보를 불러오는 중...</p>
      </div>
    )
  }

  const dayMasterEl = GAN_ELEMENT[saju.day_gan] ?? '토'
  const elInfo = ELEMENT_LABEL[dayMasterEl]
  const elements = [
    { label: '목', v: saju.element_wood },
    { label: '화', v: saju.element_fire },
    { label: '토', v: saju.element_earth },
    { label: '금', v: saju.element_metal },
    { label: '수', v: saju.element_water },
  ]
  const total = elements.reduce((s, e) => s + e.v, 0)
  const dominant = elements.reduce((a, b) => a.v >= b.v ? a : b)

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
      {/* 캐릭터 영역 */}
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
          style={{ background: elInfo.bg, border: `2px solid ${elInfo.color}40` }}>
          {elInfo.emoji}
        </div>
        <div>
          <p className="font-bold text-[var(--color-text-primary)] text-base">{nickname}님의 사주</p>
          <p className="text-sm mt-0.5" style={{ color: elInfo.color }}>
            {saju.day_gan}일간 · {dayMasterEl} 기운
          </p>
          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {mbti && <span className="text-xs bg-[var(--color-primary)]/40 text-[var(--color-secondary)] rounded-md px-2 py-0.5 font-medium">{mbti}</span>}
            {bloodType && <span className="text-xs bg-[var(--color-accent)]/10 text-[var(--color-accent)] rounded-md px-2 py-0.5 font-medium">{bloodType}형</span>}
          </div>
        </div>
      </div>

      {/* 사주팔자 */}
      <div className="grid grid-cols-4 gap-1.5 mb-3">
        {[
          { label: '년', gan: saju.year_gan, ji: saju.year_ji },
          { label: '월', gan: saju.month_gan, ji: saju.month_ji },
          { label: '일', gan: saju.day_gan, ji: saju.day_ji },
          { label: '시', gan: saju.time_gan ?? '?', ji: saju.time_ji ?? '?' },
        ].map((p) => (
          <div key={p.label} className="bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-xl p-2 text-center">
            <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">{p.label}</div>
            <div className="text-[var(--color-accent)] font-bold text-sm">{p.gan}</div>
            <div className="text-[var(--color-text-secondary)] text-xs">{p.ji}</div>
          </div>
        ))}
      </div>

      {/* 오행 바 */}
      <div>
        <p className="text-xs text-[var(--color-text-tertiary)] mb-2">오행 분포 · 강한 기운: <strong style={{ color: ELEMENT_LABEL[dominant.label].color }}>{dominant.label}({dominant.v})</strong></p>
        <div className="space-y-1.5">
          {elements.map((e) => {
            const info = ELEMENT_LABEL[e.label]
            const pct = total > 0 ? Math.round(e.v / total * 100) : 0
            return (
              <div key={e.label} className="flex items-center gap-2">
                <span className="text-xs w-4 flex-shrink-0" style={{ color: info.color }}>{e.label}</span>
                <div className="flex-1 bg-[var(--color-border-default)] rounded-full h-1.5">
                  <div className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: info.color }} />
                </div>
                <span className="text-xs w-3 flex-shrink-0 text-[var(--color-text-disabled)]">{e.v}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
